/**
 * Gstr2bReconScreen — reconcile purchases (books) against the GST portal's
 * GSTR-2B JSON to protect Input Tax Credit.
 *
 * Flow: pick a period → we load received purchases → import the GSTR-2B JSON
 * downloaded from the portal → we match on supplier GSTIN + invoice number and
 * classify each into Matched / Mismatch / Only-in-books (ITC at risk) /
 * Only-in-2B (unclaimed ITC). Results export to CSV for the CA.
 *
 * Plan-gated behind Advanced Reports (PRO); permission-gated behind reports:gst.
 */

import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Download, FileUp, RefreshCw, Scale } from 'lucide-react';
import { useAppStore } from '@/store/useAppStore';
import { usePlanGate, FEATURE_ADVANCED_REPORTS } from '@/plans';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import { startOfDay, toDateInputValue, fromDateInputValue } from '@/utils/dateRange';
import { Button } from '@/components/ui/Button';
import { file } from '@/platform';
import { loadBooksPurchases } from '@/reports/gst/purchaseDataSource';
import { loadStoreGstIdentity } from '@/reports/gst/gstDataSource';
import {
  parseGstr2bJson,
  reconcile2b,
  reconResultToCsv,
  validateGstr2bFile,
  type BooksPurchase,
  type ReconResult,
  type ReconStatus,
} from '@/reports/gst/gstr2b';

type Preset = 'month' | 'lastMonth' | 'custom';

function endOfDay(d: Date): Date { const c = new Date(d); c.setHours(23, 59, 59, 999); return c; }

function presetRange(preset: Preset, custom: [Date, Date]): [Date, Date] {
  const now = new Date();
  if (preset === 'month') return [startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), endOfDay(now)];
  if (preset === 'lastMonth') {
    const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const to = new Date(now.getFullYear(), now.getMonth(), 0);
    return [startOfDay(from), endOfDay(to)];
  }
  return custom;
}

const STATUS_META: Record<ReconStatus, { label: string; cls: string }> = {
  MATCHED:       { label: 'Matched',        cls: 'bg-success/10 text-success-ink' },
  MISMATCH:      { label: 'Mismatch',       cls: 'bg-warning/10 text-warning-ink' },
  ONLY_IN_BOOKS: { label: 'Only in books',  cls: 'bg-danger/10 text-danger-ink' },
  ONLY_IN_2B:    { label: 'Only in 2B',     cls: 'bg-accent/10 text-accent-ink' },
};

export function Gstr2bReconScreen() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const { canAccess } = usePlanGate();
  const [preset, setPreset] = useState<Preset>('month');
  const [customFrom, setCustomFrom] = useState(toDateInputValue(startOfDay(new Date(new Date().getFullYear(), new Date().getMonth(), 1))));
  const [customTo, setCustomTo] = useState(toDateInputValue(new Date()));
  const [books, setBooks] = useState<BooksPurchase[]>([]);
  const [booksLoading, setBooksLoading] = useState(true);
  const [result, setResult] = useState<ReconResult | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useErrorPopup(error, () => setError(null));
  const [busy, setBusy] = useState(false);
  const [ownGstin, setOwnGstin] = useState<string | null>(null);

  const range = useCallback((): [Date, Date] => {
    return presetRange(preset, [fromDateInputValue(customFrom), endOfDay(fromDateInputValue(customTo))]);
  }, [preset, customFrom, customTo]);

  const loadBooks = useCallback(async () => {
    if (!activeStoreId) return;
    setBooksLoading(true);
    setResult(null);
    setFileName(null);
    try {
      const [from, to] = range();
      setBooks(await loadBooksPurchases(activeStoreId, from, to));
    } finally {
      setBooksLoading(false);
    }
  }, [activeStoreId, range]);

  useEffect(() => { void loadBooks(); }, [loadBooks]);

  useEffect(() => {
    if (!activeStoreId) return;
    let alive = true;
    void loadStoreGstIdentity(activeStoreId)
      .then((id) => { if (alive) setOwnGstin(id.gstin); })
      .catch(() => { /* guard degrades to period-only check */ });
    return () => { alive = false; };
  }, [activeStoreId]);

  const handleImport = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await file.openText({
        title: 'Open GSTR-2B JSON',
        filters: [{ name: 'GSTR-2B JSON', extensions: ['json'] }],
      });
      if (!res.ok || !res.content) {
        if (!res.canceled) setError(res.message ?? 'Could not read the file.');
        return;
      }
      const parsed = parseGstr2bJson(res.content);

      // A 2B from the wrong month or the wrong taxpayer reconciles "successfully"
      // and reports every purchase as ITC at risk. Refuse it.
      const [from, to] = range();
      const problem = validateGstr2bFile(parsed, from, to, ownGstin);
      if (problem) {
        setError(problem);
        setResult(null);
        setFileName(null);
        return;
      }

      setResult(reconcile2b(books, parsed.invoices));
      setFileName(res.fileName ?? 'GSTR-2B');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse GSTR-2B.');
    } finally {
      setBusy(false);
    }
  };

  const handleExportCsv = async () => {
    if (!result) return;
    const [from] = range();
    await file.saveText({
      content: reconResultToCsv(result),
      defaultFileName: `GSTR2B_Reconciliation_${toDateInputValue(from)}.csv`,
      filters: [{ name: 'CSV', extensions: ['csv'] }],
    });
  };

  if (!canAccess(FEATURE_ADVANCED_REPORTS)) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="text-heading text-fg-primary">GSTR-2B Reconciliation requires the Pro plan</div>
          <div className="mt-1 text-body text-fg-tertiary">Upgrade to Professional or Enterprise to reconcile Input Tax Credit.</div>
        </div>
      </div>
    );
  }

  const s = result?.summary;

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {(['month', 'lastMonth', 'custom'] as Preset[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPreset(p)}
            className={cn(
              'rounded-md px-3 py-1.5 text-body transition-colors',
              preset === p ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle',
            )}
          >
            {p === 'month' ? 'This month' : p === 'lastMonth' ? 'Last month' : 'Custom'}
          </button>
        ))}
        {preset === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} max={customTo} title="From date" aria-label="From date"
              onChange={(e) => setCustomFrom(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
            <span className="text-fg-tertiary">–</span>
            <input type="date" value={customTo} min={customFrom} title="To date" aria-label="To date"
              onChange={(e) => setCustomTo(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => void loadBooks()} disabled={booksLoading}>
            <RefreshCw size={14} className={cn(booksLoading && 'animate-spin')} />
          </Button>
          {result && (
            <Button variant="secondary" onClick={() => void handleExportCsv()}>
              <Download size={14} className="mr-1.5" /> CSV
            </Button>
          )}
          <Button variant="primary" onClick={() => void handleImport()} disabled={booksLoading || busy}>
            <FileUp size={14} className="mr-1.5" /> {busy ? 'Reading…' : 'Import GSTR-2B JSON'}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 text-label text-fg-tertiary">
        <Scale size={14} />
        <span>{books.length} purchase{books.length === 1 ? '' : 's'} in books for this period{fileName ? ` · matched against ${fileName}` : ''}.</span>
      </div>

      {!result ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-stroke-hairline/20 py-16 text-center">
          <FileUp size={28} className="text-fg-tertiary" />
          <p className="text-body text-fg-secondary">Import the GSTR-2B JSON downloaded from the GST portal to reconcile.</p>
          <p className="max-w-md text-label text-fg-tertiary">
            Portal → Returns → GSTR-2B → Download (JSON). We match on supplier GSTIN + invoice number.
          </p>
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <SummaryCard tone="success" icon={<CheckCircle2 size={16} />} label="Matched" count={s!.matched} amount={s!.itcMatched} amountLabel="ITC safe" />
            <SummaryCard tone="warning" icon={<AlertTriangle size={16} />} label="Mismatch" count={s!.mismatch} amount={s!.itcMismatchDiff} amountLabel="Tax diff" />
            <SummaryCard tone="danger" icon={<AlertTriangle size={16} />} label="Only in books" count={s!.onlyInBooks} amount={s!.itcOnlyInBooks} amountLabel="ITC at risk" />
            <SummaryCard tone="accent" icon={<AlertTriangle size={16} />} label="Only in 2B" count={s!.onlyIn2b} amount={s!.itcOnlyIn2b} amountLabel="Unclaimed ITC" />
          </div>

          {/* Rows */}
          <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3 overflow-x-auto">
            <table className="w-full text-body">
              <thead>
                <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                  <th className="py-1 text-left font-normal">Status</th>
                  <th className="py-1 text-left font-normal">Supplier / GSTIN</th>
                  <th className="py-1 text-left font-normal">Invoice</th>
                  <th className="py-1 text-right font-normal">Books tax</th>
                  <th className="py-1 text-right font-normal">2B tax</th>
                  <th className="py-1 text-right font-normal">Diff</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.map((r, i) => (
                  <tr key={i} className="border-b border-stroke-hairline/10">
                    <td className="py-1.5">
                      <span className={cn('rounded-sm px-2 py-0.5 text-label uppercase', STATUS_META[r.status].cls)}>
                        {STATUS_META[r.status].label}
                      </span>
                      {r.matchType === 'FUZZY' && (
                        <span className="ml-1 rounded-sm bg-bg-subtle px-1.5 py-0.5 text-label uppercase text-fg-tertiary" title="Paired by fuzzy invoice-number / date matching — verify before claiming">
                          ~fuzzy
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 text-fg-primary">{r.supplierName}<br /><span className="font-mono text-label text-fg-tertiary">{r.supplierGstin || '—'}</span></td>
                    <td className="py-1.5 font-mono text-label text-fg-secondary">
                      {r.invoiceNumber}
                      {r.matchType === 'FUZZY' && r.twoB?.invoiceNumber && r.twoB.invoiceNumber !== r.invoiceNumber && (
                        <><br /><span className="text-fg-tertiary">2B: {r.twoB.invoiceNumber}</span></>
                      )}
                    </td>
                    <td className="py-1.5 text-right text-fg-secondary tabular-nums">{r.books ? formatCurrency(r.books.totalTax) : '—'}</td>
                    <td className="py-1.5 text-right text-fg-secondary tabular-nums">{r.twoB ? formatCurrency(r.twoB.totalTax) : '—'}</td>
                    <td className={cn('py-1.5 text-right tabular-nums', r.taxDiff && Math.abs(r.taxDiff) > 0 ? 'text-warning-ink' : 'text-fg-tertiary')}>
                      {r.taxDiff != null ? formatCurrency(r.taxDiff) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({
  tone, icon, label, count, amount, amountLabel,
}: {
  tone: 'success' | 'warning' | 'danger' | 'accent';
  icon: React.ReactNode;
  label: string;
  count: number;
  amount: number;
  amountLabel: string;
}) {
  const toneCls: Record<string, string> = {
    success: 'text-success-ink', warning: 'text-warning-ink', danger: 'text-danger-ink', accent: 'text-accent-ink',
  };
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className={cn('flex items-center gap-1.5 text-label uppercase', toneCls[tone])}>{icon}{label}</div>
      <div className="mt-1 text-heading tabular-nums text-fg-primary">{count}</div>
      <div className="text-label text-fg-tertiary">{amountLabel}: <span className="tabular-nums">{formatCurrency(amount)}</span></div>
    </div>
  );
}
